import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// ワークフロー編集モーダル。ETLWorkflowPage から props 経由で利用する。
import { useState, useEffect } from 'react';
import EmployeeSelectorModal, { EmployeeSelectionSummary, resolveEmployeesByNumbers, } from '../../components/EmployeeSelectorModal';
import Modal from '../../components/Modal';
import { apiClient } from '../../utils/apiClient';
import toast from 'react-hot-toast';
import { assertWizardSupportedByV2, buildAgentWorkflowPayload } from '../../utils/workflowComposer';
import { isAgentTransformStep, isWorkflowEditableByV2Steps } from './etlWorkflowShared';
const WorkflowEditModal = ({ workflow, tenantId, onClose, onSuccess }) => {
    // MongoDB保存形式対応：etl_config.stepsをstepsとして使用
    const actualSteps = workflow.steps || workflow.etl_config?.steps || [];
    const [formData, setFormData] = useState({
        name: workflow.name,
        description: workflow.description || '',
        is_active: workflow.is_active !== false
    });
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const isWorkflowEditableByV2 = isWorkflowEditableByV2Steps(actualSteps);
    const persistedKujyuStep = actualSteps.find((s) => s.step_kind === 'kujyu_reports_extract');
    // 対象社員（週次レポート）の編集状態。保存値は社員番号のみなので氏名は後から解決する
    const [editEmployees, setEditEmployees] = useState(() => {
        const numbers = persistedKujyuStep?.config?.employee_numbers;
        return Array.isArray(numbers)
            ? numbers.map((n) => ({ employeeNumber: n, name: n }))
            : [];
    });
    const [employeeSelectorOpen, setEmployeeSelectorOpen] = useState(false);
    // 保存済みの社員番号に氏名を補完する（番号ごとの単発照会・表示用のベストエフォート）
    useEffect(() => {
        if (!persistedKujyuStep || editEmployees.length === 0)
            return;
        let cancelled = false;
        resolveEmployeesByNumbers(editEmployees.map((e) => e.employeeNumber))
            .then((resolved) => {
            if (!cancelled)
                setEditEmployees(resolved);
        })
            .catch((error) => {
            console.error('対象社員の氏名解決に失敗しました:', error);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // 拡張編集項目の状態管理（MongoDB形式対応）
    const [editDataSourceConfig, setEditDataSourceConfig] = useState(() => {
        const extractStep = actualSteps.find((s) => s.step_type === 'extract');
        const type = extractStep?.step_kind === 'upload_file_extract'
            ? 'file'
            : extractStep?.step_kind === 'kujyu_reports_extract'
                ? 'kujyu'
                : (extractStep?.config?.data_source || 'none');
        return {
            type,
            config: extractStep?.config || {},
        };
    });
    const [editAgentConfig, setEditAgentConfig] = useState(() => {
        const transformStep = actualSteps.find((s) => s.step_type === 'transform');
        const agentId = transformStep?.config?.agent_id || transformStep?.agent_config?.agent_id || '';
        const agentName = transformStep?.config?.agent_name || '';
        return {
            selectedAgentId: agentId,
            agentName: agentName
        };
    });
    // AgentTransformStep ⇔ AgentMapWorkflowTransformStep の切り替え（既存 step から初期化）
    const [editUseMapTransform, setEditUseMapTransform] = useState(() => {
        const transformStep = actualSteps.find((s) => s.step_type === 'transform');
        return transformStep?.step_kind === 'agent_map_transform';
    });
    const [editNotificationConfig, setEditNotificationConfig] = useState(() => {
        const loadStep = actualSteps.find((s) => s.step_type === 'load');
        const channels = loadStep?.config?.channels || [];
        const targetType = loadStep?.config?.notification_type || 'all';
        return {
            channels: channels,
            targetType: targetType
        };
    });
    // エージェント・チャネル一覧（編集用）
    const [availableAgents, setAvailableAgents] = useState([]);
    const [availableChannels, setAvailableChannels] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [channelsLoading, setChannelsLoading] = useState(false);
    // 編集用エージェント・チャネル一覧取得
    useEffect(() => {
        const fetchEditData = async () => {
            // エージェント一覧取得
            setAgentsLoading(true);
            try {
                const agentsResponse = await apiClient.mizu.dive.listAgents();
                if (agentsResponse.success && agentsResponse.data) {
                    const responseData = agentsResponse.data;
                    const agentsList = responseData.data || responseData;
                    setAvailableAgents(Array.isArray(agentsList) ? agentsList : [agentsList]);
                    console.log('✅ 編集用エージェント取得成功:', agentsList.length);
                }
            }
            catch (error) {
                console.error('❌ 編集用エージェント取得エラー:', error);
            }
            finally {
                setAgentsLoading(false);
            }
            // 通知チャネル一覧取得（実際のチャンネル管理から）
            setChannelsLoading(true);
            try {
                const channelsResponse = await apiClient.get('/api/v1/channels');
                if (channelsResponse.success && channelsResponse.data) {
                    const responseData = channelsResponse.data;
                    const channelsData = responseData?.channels || [];
                    const channels = [];
                    // 実際のチャンネルをマッピング
                    for (const channel of channelsData) {
                        const providerEmojiMap = {
                            'slack': '📢',
                            'email': '📧',
                            'google_chat': '💬',
                            'microsoft_teams': '🏢',
                            'discord': '🎮',
                            'websocket': '🔔'
                        };
                        const emoji = providerEmojiMap[channel.provider] || '📱';
                        channels.push({
                            id: channel.id,
                            label: `${emoji} ${channel.name}`,
                            enabled: true,
                            provider: channel.provider,
                            description: channel.description || ''
                        });
                    }
                    setAvailableChannels(channels);
                    console.log('✅ 編集用チャンネル取得成功:', channels.length);
                }
            }
            catch (error) {
                console.error('❌ 編集用チャンネル取得エラー:', error);
            }
            finally {
                setChannelsLoading(false);
            }
        };
        fetchEditData();
    }, []);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            setError('ワークフロー名は必須です');
            return;
        }
        setIsUpdating(true);
        setError(null);
        try {
            assertWizardSupportedByV2({
                dataSourceType: isWorkflowEditableByV2 ? editDataSourceConfig.type : 'legacy',
                processingMode: isWorkflowEditableByV2 ? 'agent' : 'legacy',
                hasNotificationStep: !isWorkflowEditableByV2,
            });
            const persistedAgentStep = actualSteps.find(isAgentTransformStep);
            const agentId = editAgentConfig.selectedAgentId || persistedAgentStep?.config?.agent_id;
            if (!agentId) {
                throw new Error('このワークフローは workflow v2 の agent wizard では編集できません。');
            }
            const updateData = buildAgentWorkflowPayload({
                workflow_id: workflow.workflow_id,
                name: formData.name,
                description: formData.description,
                tags: workflow.tags || [],
                agent_id: agentId,
                file_ids: editDataSourceConfig.type === 'file' ? editDataSourceConfig.config.file_ids : undefined,
                useMapTransform: editUseMapTransform,
                kujyu_reports: persistedKujyuStep
                    ? {
                        unit: persistedKujyuStep.config?.unit,
                        offset: Number(persistedKujyuStep.config?.offset),
                        employee_numbers: editEmployees.map((e) => e.employeeNumber),
                    }
                    : undefined,
            });
            const response = await apiClient.susugi.etl.updateWorkflow(tenantId, workflow.workflow_id, updateData);
            if (response.success && response.data) {
                console.log('✅ ワークフロー更新成功:', response.data);
                const updatedWorkflow = response.data.workflow || {
                    ...workflow,
                    name: formData.name,
                    description: formData.description,
                    is_active: formData.is_active,
                    updated_at: new Date().toISOString()
                };
                onSuccess(updatedWorkflow);
                toast.success(`ワークフロー「${formData.name}」を更新しました`);
            }
            else {
                console.error('❌ ワークフロー更新失敗:', response.error);
                setError(response.error || 'ワークフローの更新に失敗しました');
            }
        }
        catch (error) {
            console.error('❌ ワークフロー更新エラー:', error);
            setError(error.message || '不明なエラー');
        }
        finally {
            setIsUpdating(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Modal, { isOpen: true, onClose: onClose, size: "lg", title: "\u270F\uFE0F \u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u7DE8\u96C6", footer: _jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: onClose, className: "px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50", children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), _jsx("button", { type: "submit", form: "workflow-edit-form", disabled: isUpdating || !isWorkflowEditableByV2, className: `px-4 py-2 text-sm font-medium rounded-md ${isUpdating || !isWorkflowEditableByV2
                                ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                                : 'text-white bg-indigo-600 hover:bg-indigo-700'}`, children: isUpdating ? '更新中...' : '更新' })] }), children: [_jsxs("p", { className: "text-sm text-gray-600 mb-4", children: ["\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u300C", workflow.name, "\u300D\u306E\u8A2D\u5B9A\u3092\u7DE8\u96C6\u3057\u307E\u3059"] }), _jsxs("form", { id: "workflow-edit-form", onSubmit: handleSubmit, className: "space-y-6", children: [_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: ["\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u540D ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), className: "w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500", placeholder: "\u4F8B: \u55B6\u696D\u30C7\u30FC\u30BF\u5206\u6790\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC", required: true })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u8AAC\u660E" }), _jsx("textarea", { value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), className: "w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500", rows: 3, placeholder: "\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u306E\u76EE\u7684\u30FB\u51E6\u7406\u5185\u5BB9\u3092\u8AAC\u660E\u3057\u3066\u304F\u3060\u3055\u3044" })] }), !isWorkflowEditableByV2 && (_jsx("div", { className: "rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900", children: "\u3053\u306E\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u306B\u306F workflow v2 editor \u304C\u672A\u5BFE\u5FDC\u306E\u5206\u5C90\u304C\u542B\u307E\u308C\u3066\u3044\u307E\u3059\u3002 \u65E7 extract/load/notification \u3092\u542B\u3080\u5B9A\u7FA9\u306F\u3053\u3053\u3067\u306F\u66F4\u65B0\u3067\u304D\u307E\u305B\u3093\u3002\u65B0\u898F\u4F5C\u6210\u3067\u4F5C\u308A\u76F4\u3057\u3066\u304F\u3060\u3055\u3044\u3002" })), isWorkflowEditableByV2 && _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\uD83E\uDD16 \u51E6\u7406\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8" }), agentsLoading ? (_jsxs("div", { className: "flex items-center py-4", children: [_jsx("div", { className: "animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2" }), _jsx("span", { className: "text-sm text-gray-600", children: "\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u4E00\u89A7\u3092\u53D6\u5F97\u4E2D..." })] })) : (_jsxs("select", { value: editAgentConfig.selectedAgentId, onChange: (e) => {
                                            const selectedAgent = availableAgents.find(agent => (agent.agent_id || agent.id) === e.target.value);
                                            setEditAgentConfig({
                                                selectedAgentId: e.target.value,
                                                agentName: selectedAgent?.name || selectedAgent?.agent_name || ''
                                            });
                                        }, className: "w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500", children: [_jsx("option", { value: "", children: "\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u3092\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044" }), availableAgents.map((agent) => {
                                                const agentId = agent.agent_id || agent.id;
                                                const agentName = agent.name || agent.agent_name || '不明なエージェント';
                                                return (_jsx("option", { value: agentId, children: agentName }, agentId));
                                            })] })), editAgentConfig.selectedAgentId && (_jsxs("p", { className: "text-xs text-gray-500 mt-1", children: ["\u9078\u629E\u4E2D: ", editAgentConfig.agentName] }))] }), isWorkflowEditableByV2 && (_jsxs("div", { className: "flex items-center justify-between border border-gray-200 rounded-lg p-4", children: [_jsxs("div", { className: "pr-4", children: [_jsx("div", { className: "font-medium text-gray-900 text-sm", children: "\u30EA\u30B9\u30C8\u5206\u5272\u5B9F\u884C\uFF08Map\uFF09\u30E2\u30FC\u30C9" }), _jsx("div", { className: "text-xs text-gray-500 mt-1", children: "\u5165\u529B\u30EA\u30B9\u30C8\u3092\u9805\u76EE\u3054\u3068\u306B\u5206\u5272\u3057\u3001\u5404\u9805\u76EE\u3092\u500B\u5225\u306E\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u5B9F\u884C\u3068\u3057\u3066\u4E26\u5217\u51E6\u7406\u3057\u307E\u3059\u3002" })] }), _jsx("button", { type: "button", role: "switch", "aria-checked": editUseMapTransform, onClick: () => setEditUseMapTransform((value) => !value), className: `relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${editUseMapTransform ? 'bg-indigo-600' : 'bg-gray-300'}`, children: _jsx("span", { className: `inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${editUseMapTransform ? 'translate-x-5' : 'translate-x-0.5'}` }) })] })), isWorkflowEditableByV2 && _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\uD83D\uDCC1 \u30C7\u30FC\u30BF\u30BD\u30FC\u30B9\uFF08\u53C2\u8003\u60C5\u5831\uFF09" }), _jsxs("div", { className: "bg-gray-50 border border-gray-200 rounded-md p-3", children: [_jsxs("div", { className: "text-sm text-gray-700", children: ["\u7A2E\u985E: ", editDataSourceConfig.type === 'file' ? '📁 ファイルアップロード' :
                                                        editDataSourceConfig.type === 'kujyu' ? '🏢 社内データ取得' :
                                                            editDataSourceConfig.type === 'bigquery' ? '📊 BigQuery SQL' : '不明'] }), _jsx("div", { className: "text-xs text-gray-500 mt-1", children: "\u203B \u30C7\u30FC\u30BF\u30BD\u30FC\u30B9\u306E\u5909\u66F4\u306F\u65B0\u898F\u30EF\u30FC\u30AF\u30D5\u30ED\u30FC\u3092\u4F5C\u6210\u3057\u3066\u304F\u3060\u3055\u3044" }), persistedKujyuStep && (_jsxs("div", { className: "mt-3 pt-3 border-t border-gray-200", children: [_jsx("label", { className: "block text-xs font-medium text-gray-700 mb-1", children: "\u5BFE\u8C61\u793E\u54E1\uFF08\u9031\u6B21\u30EC\u30DD\u30FC\u30C8\uFF09" }), _jsx(EmployeeSelectionSummary, { employees: editEmployees, onOpen: () => setEmployeeSelectorOpen(true) }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "\u672A\u9078\u629E\u306E\u5834\u5408\u306F\u5168\u793E\u54E1\u3092\u53D6\u5F97\u3057\u307E\u3059\u3002" })] }))] })] }), _jsx("div", { className: "rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600", children: "workflow v2 editor \u3067\u306F\u30A2\u30AF\u30C6\u30A3\u30D6\u72B6\u614B\u306E\u5909\u66F4\u306F\u307E\u3060\u30B5\u30DD\u30FC\u30C8\u3055\u308C\u3066\u3044\u307E\u305B\u3093\u3002" }), error && (_jsx("div", { className: "bg-red-50 border border-red-200 rounded-md p-3", children: _jsxs("div", { className: "text-sm text-red-800", children: [_jsx("strong", { children: "\u30A8\u30E9\u30FC:" }), " ", error] }) }))] })] }), _jsx(EmployeeSelectorModal, { isOpen: employeeSelectorOpen, onClose: () => setEmployeeSelectorOpen(false), selectedEmployees: editEmployees, onConfirm: setEditEmployees })] }));
};
export default WorkflowEditModal;
